/**
 * autoplayOverlayGeometry
 * -----------------------
 * Single source of truth for the autoplay-overlay box geometry.
 *
 * Used by:
 *   • AutoplayResizeWrapper (editor drag/resize)
 *   • AutoplayOverlay (runtime preview)
 *   • e-player-js/js/runner.js (CDN runtime) — mirrored inline since the
 *     embed cannot import ESM. Keep both in sync byte-for-byte.
 *
 * Pure math. No React. Plain numbers in/out.
 */

export type Breakpoint = 'desktop' | 'tablet' | 'mobile';

export interface BreakpointConstraints {
  /** Min width in % of container. */
  minWidthPercent: number;
  /** Max width in % of container. */
  maxWidthPercent: number;
  /** Min height in % of container (when height is fixed). */
  minHeightPercent: number;
  /** Max height in % of container (when height is fixed). */
  maxHeightPercent: number;
  /** Approx height when heightMode='auto'. */
  estimatedAutoHeightPercent: number;
  /** Safe area padding from each edge (% of container). */
  safeAreaPercent: number;
}

/**
 * Per-breakpoint constraints. Mirrored in e-player-js/js/runner.js.
 * Change BOTH places together.
 */
export const BREAKPOINT_CONSTRAINTS: Record<Breakpoint, BreakpointConstraints> = {
  desktop: {
    minWidthPercent: 18,
    maxWidthPercent: 47,
    minHeightPercent: 12,
    maxHeightPercent: 60,
    estimatedAutoHeightPercent: 28,
    safeAreaPercent: 2,
  },
  tablet: {
    minWidthPercent: 28,
    maxWidthPercent: 70,
    minHeightPercent: 14,
    maxHeightPercent: 70,
    estimatedAutoHeightPercent: 32,
    safeAreaPercent: 3,
  },
  mobile: {
    minWidthPercent: 50,
    maxWidthPercent: 92,
    minHeightPercent: 16,
    maxHeightPercent: 80,
    estimatedAutoHeightPercent: 38,
    safeAreaPercent: 4,
  },
};

export const BREAKPOINT_THRESHOLDS = {
  mobileMaxPx: 540,
  tabletMaxPx: 960,
} as const;

export function getBreakpoint(containerWidthPx: number): Breakpoint {
  if (containerWidthPx < BREAKPOINT_THRESHOLDS.mobileMaxPx) return 'mobile';
  if (containerWidthPx < BREAKPOINT_THRESHOLDS.tabletMaxPx) return 'tablet';
  return 'desktop';
}

export function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

export interface AutoplayOverlayInput {
  position?: { xPercent?: number; yPercent?: number };
  size?: {
    widthPercent?: number;
    heightMode?: 'auto' | 'fixed';
    heightPercent?: number;
  };
}

export interface ClampedLayout {
  xPercent: number;
  yPercent: number;
  widthPercent: number;
  heightPercent: number;
  bounds: { minX: number; maxX: number; minY: number; maxY: number };
}

export const DEFAULTS = {
  xPercent: 50,
  yPercent: 50,
  widthPercent: 34,
  heightMode: 'auto' as const,
} as const;

const MEMO_LIMIT = 32;
const memoCache = new Map<string, ClampedLayout>();

function buildCacheKey(overlay: AutoplayOverlayInput | undefined, breakpoint: Breakpoint): string {
  const pos = overlay?.position;
  const size = overlay?.size;
  return [
    breakpoint,
    pos?.xPercent ?? DEFAULTS.xPercent,
    pos?.yPercent ?? DEFAULTS.yPercent,
    size?.widthPercent ?? DEFAULTS.widthPercent,
    size?.heightMode ?? DEFAULTS.heightMode,
    size?.heightPercent ?? -1,
  ].join('|');
}

function computeClampedLayout(
  overlay: AutoplayOverlayInput | undefined,
  breakpoint: Breakpoint,
): ClampedLayout {
  const c = BREAKPOINT_CONSTRAINTS[breakpoint];
  const pos = overlay?.position ?? {};
  const size = overlay?.size ?? {};

  const widthPercent = clamp(
    size.widthPercent ?? DEFAULTS.widthPercent,
    c.minWidthPercent,
    c.maxWidthPercent,
  );

  const rawHeight =
    size.heightMode === 'fixed' && size.heightPercent != null
      ? size.heightPercent
      : c.estimatedAutoHeightPercent;
  const heightPercent = clamp(rawHeight, c.minHeightPercent, c.maxHeightPercent);

  const halfW = widthPercent / 2;
  const halfH = heightPercent / 2;
  const minX = halfW + c.safeAreaPercent;
  const maxX = 100 - halfW - c.safeAreaPercent;
  // Y é mais permissivo: anchor (center) pode ir de 10% a 90% pra todos
  // templates/breakpoints. Não considera halfH — overlay grande posicionado
  // no extremo VAI cortar visualmente; é decisão consciente do usuário.
  const minY = 10;
  const maxY = 90;

  const xPercent = minX > maxX ? 50 : clamp(pos.xPercent ?? DEFAULTS.xPercent, minX, maxX);
  const yPercent = clamp(pos.yPercent ?? DEFAULTS.yPercent, minY, maxY);

  return {
    xPercent,
    yPercent,
    widthPercent,
    heightPercent,
    bounds: { minX, maxX, minY, maxY },
  };
}

/**
 * Clamp position+size of the autoplay overlay against the breakpoint's
 * safe area. Center-anchored (translate(-50%, -50%)) — bounds account for it.
 * Pure, deterministic, memoized.
 */
export function clampAutoplayOverlayLayout(
  overlay: AutoplayOverlayInput | undefined,
  breakpoint: Breakpoint,
): ClampedLayout {
  const key = buildCacheKey(overlay, breakpoint);
  const cached = memoCache.get(key);
  if (cached) {
    memoCache.delete(key);
    memoCache.set(key, cached);
    return cached;
  }
  const result = Object.freeze(computeClampedLayout(overlay, breakpoint)) as ClampedLayout;
  memoCache.set(key, result);
  if (memoCache.size > MEMO_LIMIT) {
    const oldest = memoCache.keys().next().value;
    if (oldest !== undefined) memoCache.delete(oldest);
  }
  return result;
}
